import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline: 'border border-input bg-background shadow-xs hover:bg-accent/10',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent/10',
        link: 'text-primary underline-offset-4 hover:underline',
        // THE TUTORIAL COLOUR (#14018). The Quick Start walks a user across the real product,
        // so at any moment the page shows both "the tutorial's next move" and "the product's
        // own actions" — and while both were `default` they were the same filled blue, which
        // made the two indistinguishable mid-tour. Green is the tutorial's; blue stays the
        // product's. Reported from the live demo: "the tutorial go forward, hit the green one;
        // the product is blue."
        //
        // A VARIANT, not a `className` at each call site: there are ~20 quickstart buttons
        // across the hub, the panels and the wait screens, and a per-site literal drifts the
        // first time one is copied. `components/quickstart/tutorial-button-variant.test.ts`
        // fails if a quickstart button falls back to `default`, and the driver.js popover's own
        // buttons are matched in `tour-overlay.css` so the two surfaces cannot disagree.
        //
        // `success` is the existing green token (defined for BOTH themes, `--success`), not a
        // new colour — see app/globals.css.
        tutorial: 'bg-success text-success-foreground shadow-xs hover:bg-success/90',
        // The tutorial colour for a LINK-weight control — a tutorial action that should not
        // carry a button's weight (the events side-trip, "open your offering"). Same job as
        // `link`, which is `text-primary` and therefore the PRODUCT's blue: a control that
        // launches a tutorial excursion must not look like one of the product's own links.
        //
        // NOT `text-success`. As a fill that token is fine (white on green), but as TEXT it
        // measures 2.54:1 on white — under the 4.5:1 AA floor. Measured, not guessed:
        // emerald-700 is 5.48:1 on white (the blue it replaces is 5.57:1) and emerald-400 is
        // 10.3:1 on the dark ground. `tutorial-button-variant.test.ts` re-computes both from
        // these literals, so a future re-shade cannot quietly drop below the floor.
        tutorialLink: 'text-emerald-700 underline-offset-4 hover:underline dark:text-emerald-400',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
